import { useEffect } from "react";

import EditableReportSubSection from "@app/analytics/MeetingReport/components/EditableReportSubSection";
import { EDITING_TOOLTIP } from "@app/analytics/MeetingReport/constants";
import useSessionMetadata from "@app/analytics/MeetingReport/hooks/useSessionMetadata";
import { useMeetingReportContext } from "@app/analytics/MeetingReport/providers/MeetingReportContext";
import useSearchStore from "@app/analytics/MeetingReport/stores/search";
import { elementIsVisible } from "@app/common/hooks/useVisibility";

import MeetingReportRecapCharts, {
  MeetingReportRecapChartsProps,
} from "./MeetingReportRecapCharts";
import MeetingReportRecapNotes from "./MeetingReportRecapNotes";

const getElementBySearchIndex = (searchIndex?: number) => {
  if (searchIndex == null) return null;
  return document.querySelector(`[data-search-index="${searchIndex}"]`);
};

export interface RecapNotesSectionProps {
  asTab?: boolean;
  chartProps: MeetingReportRecapChartsProps;
  showCharts: boolean;
}

const RecapNotesSection = ({
  asTab = false,
  chartProps,
  showCharts,
}: RecapNotesSectionProps) => {
  const { mainScrollContainer } = useMeetingReportContext();
  const {
    data: {
      isNotetakerReport,
      isRestrictedReport,
      shouldUpsellTranscriptEntities,
    },
  } = useSessionMetadata();
  const activeSearchResult = useSearchStore(
    (state) => state.searchResults[state.activeSearchResultIndex]
  );

  useEffect(() => {
    if (activeSearchResult?.type !== "note_search_result") {
      // the transcript turns handle scrolling internally, since any DOM
      // searching isn't reliable with the virtualized transcript
      return;
    }

    const resultRef = getElementBySearchIndex(activeSearchResult.index);
    if (!resultRef) return;

    const resultIsVisible = elementIsVisible(
      resultRef as HTMLElement,
      mainScrollContainer.current,
      { topOffset: 100, bottomOffset: 50 }
    );

    if (resultIsVisible) return;

    resultRef.scrollIntoView({
      behavior: "auto",
      block: "center",
      inline: "center",
    });
  }, [activeSearchResult]);

  return (
    <EditableReportSubSection
      id="notes"
      disabledTooltip={EDITING_TOOLTIP.editingTranscript}
    >
      {showCharts && <MeetingReportRecapCharts {...chartProps} />}
      <MeetingReportRecapNotes
        inMobileView={asTab}
        isNotetakerReport={isNotetakerReport}
        isRestrictedReport={isRestrictedReport}
        shouldUpsell={shouldUpsellTranscriptEntities}
      />
    </EditableReportSubSection>
  );
};

export default RecapNotesSection;
